import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { v4 as uuid_v4 } from 'uuid';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import { ReopenExtraction, ReopenImpact, ReopenManualRoom } from '~/types/reopen';
import { CatalogRoom, ExtractedDateRange, StopsellApplyPayload } from '~/types/stopsell';

import { calculateReopenPayloadParts, mergeDateRanges } from '~/utils/mergeDateRanges';
import { calculateAllReopenImpacts } from '~/utils/reopenImpact';
import { pluralizeToString } from '~/utils/stringUtils';

import AvailabilityManualRoomsSection from './AvailabilityManualRoomsSection';
import ReopenManualRoomCard from './ReopenManualRoomCard';
import ReopenRoomGroupRow from './ReopenRoomGroupRow';

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (payload: StopsellApplyPayload) => void;
  extraction: ReopenExtraction;
  fileName: string;
  catalogRooms: Array<CatalogRoom>;
  isBlackoutError?: boolean;
  isApplying?: boolean;
}

function initLocalDates(
  extraction: ReopenExtraction,
  catalogRooms: Array<CatalogRoom>,
): Array<Array<ExtractedDateRange>> {
  return extraction.rooms.map((room) => {
    const blackouts = catalogRooms.find((c) => c.id === room.roomTypeId)?.blackoutDates ?? [];
    return room.dates.map((date) => {
      const impacts = calculateAllReopenImpacts(date, blackouts);
      return { id: uuid_v4(), start: date.start, end: date.end, selected: impacts.length > 0 };
    });
  });
}

export default function ReopenReviewModal({
  open,
  onClose,
  onApply,
  extraction,
  fileName,
  catalogRooms,
  isBlackoutError = false,
  isApplying = false,
}: Props) {
  const [roomTypeOverrides, setRoomTypeOverrides] = useState<Record<number, string>>({});
  const [manualRooms, setManualRooms] = useState<Array<ReopenManualRoom>>([]);
  const [localDates, setLocalDates] = useState<Array<Array<ExtractedDateRange>>>(() =>
    initLocalDates(extraction, catalogRooms),
  );
  const [deselectedBlockIds, setDeselectedBlockIds] = useState<Set<string>>(new Set());

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setRoomTypeOverrides({});
      setManualRooms([]);
      setLocalDates(initLocalDates(extraction, catalogRooms));
      setDeselectedBlockIds(new Set());
    }
    prevOpenRef.current = open;
  }, [open, extraction, catalogRooms]);

  const impacts = useMemo<Array<Array<Array<ReopenImpact>>>>(
    () =>
      localDates.map((roomDates, roomIndex) => {
        const roomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
        const blackouts = catalogRooms.find((c) => c.id === roomTypeId)?.blackoutDates ?? [];
        return roomDates.map((date) => calculateAllReopenImpacts(date, blackouts));
      }),
    [localDates, catalogRooms, roomTypeOverrides, extraction.rooms],
  );

  const allSelected =
    localDates.every((room, ri) => room.every((date, di) => (impacts[ri]?.[di]?.length ?? 0) === 0 || date.selected)) &&
    manualRooms.every((room) => room.dates.every((d) => d.impacts.length === 0 || d.selected));

  const anySelected =
    localDates.some((room, ri) => room.some((date, di) => (impacts[ri]?.[di]?.length ?? 0) > 0 && date.selected)) ||
    manualRooms.some((room) => room.dates.some((d) => d.impacts.length > 0 && d.selected));

  const selectAllIndeterminate = anySelected && !allSelected;

  const extractedRoomTypeIds = useMemo(
    () =>
      new Set(
        extraction.rooms
          .map((room, roomIndex) => roomTypeOverrides[roomIndex] ?? room.roomTypeId)
          .filter((id): id is string => id !== null),
      ),
    [extraction.rooms, roomTypeOverrides],
  );

  const catalogRoomById = useMemo(() => new Map(catalogRooms.map((r) => [r.id, r])), [catalogRooms]);

  const allSelectedRoomsMapped = extraction.rooms.every((room, roomIndex) => {
    const isRoomSelected = localDates[roomIndex]?.some(
      (date, di) => date.selected && (impacts[roomIndex]?.[di]?.length ?? 0) > 0,
    );
    if (!isRoomSelected) return true;
    return !!(room.roomTypeId ?? roomTypeOverrides[roomIndex]);
  });

  const selectedRoomCount =
    localDates.filter((room, ri) => room.some((date, di) => date.selected && (impacts[ri]?.[di]?.length ?? 0) > 0))
      .length + manualRooms.filter((room) => room.dates.some((d) => d.selected && d.impacts.length > 0)).length;

  const selectedDateCount =
    localDates.reduce(
      (sum, room, ri) => sum + room.filter((date, di) => date.selected && (impacts[ri]?.[di]?.length ?? 0) > 0).length,
      0,
    ) + manualRooms.reduce((sum, room) => sum + room.dates.filter((d) => d.selected && d.impacts.length > 0).length, 0);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setLocalDates((prev) =>
        prev.map((room, ri) =>
          room.map((date, di) => ((impacts[ri]?.[di]?.length ?? 0) > 0 ? { ...date, selected: checked } : date)),
        ),
      );
      setManualRooms((prev) =>
        prev.map((room) => ({
          ...room,
          dates: room.dates.map((d) => (d.impacts.length > 0 ? { ...d, selected: checked } : d)),
        })),
      );
    },
    [impacts],
  );

  const handleSelectAllChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => handleSelectAll(e.target.checked),
    [handleSelectAll],
  );

  const handleRoomToggleAll = useCallback(
    (roomIndex: number, value: boolean) => {
      setLocalDates((prev) =>
        prev.map((room, ri) =>
          ri === roomIndex
            ? room.map((date, di) =>
                (impacts[roomIndex]?.[di]?.length ?? 0) > 0 ? { ...date, selected: value } : date,
              )
            : room,
        ),
      );
    },
    [impacts],
  );

  const handleDateChange = useCallback((roomIndex: number, dateIndex: number, value: boolean) => {
    setLocalDates((prev) =>
      prev.map((room, ri) =>
        ri === roomIndex ? room.map((date, di) => (di === dateIndex ? { ...date, selected: value } : date)) : room,
      ),
    );
  }, []);

  const handleRoomTypeOverride = useCallback(
    (roomIndex: number, roomTypeId: string) => {
      setRoomTypeOverrides((prev) => ({ ...prev, [roomIndex]: roomTypeId }));

      const prevRoomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
      const prevBlackouts = catalogRooms.find((c) => c.id === prevRoomTypeId)?.blackoutDates ?? [];
      const blackouts = catalogRooms.find((c) => c.id === roomTypeId)?.blackoutDates ?? [];

      setLocalDates((prev) =>
        prev.map((room, ri) => {
          if (ri !== roomIndex) return room;
          return room.map((date) => {
            const prevImpacts = calculateAllReopenImpacts(date, prevBlackouts);
            const newImpacts = calculateAllReopenImpacts(date, blackouts);
            // Auto-select only on first-time impact gain so re-mapping preserves user's choice.
            const selected = newImpacts.length === 0 ? false : prevImpacts.length === 0 ? true : date.selected;
            return { ...date, selected };
          });
        }),
      );
    },
    [catalogRooms, roomTypeOverrides, extraction.rooms],
  );

  const handleEditDate = useCallback(
    (roomIndex: number, dateId: string, newRange: { start: string; end: string }) => {
      const roomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
      const blackouts = catalogRooms.find((c) => c.id === roomTypeId)?.blackoutDates ?? [];
      const newImpacts = calculateAllReopenImpacts(newRange, blackouts);

      setLocalDates((prev) =>
        prev.map((room, ri) =>
          ri === roomIndex
            ? room.map((d) =>
                d.id === dateId ? { ...d, ...newRange, selected: newImpacts.length > 0 ? d.selected : false } : d,
              )
            : room,
        ),
      );
    },
    [catalogRooms, extraction.rooms, roomTypeOverrides],
  );

  const handleDeleteDate = useCallback((roomIndex: number, dateId: string) => {
    setLocalDates((prev) => prev.map((room, ri) => (ri === roomIndex ? room.filter((d) => d.id !== dateId) : room)));
  }, []);

  const handleAddExtractedDate = useCallback(
    (roomIndex: number, start: string, end: string) => {
      const roomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
      const blackouts = catalogRooms.find((c) => c.id === roomTypeId)?.blackoutDates ?? [];
      const newImpacts = calculateAllReopenImpacts({ start, end }, blackouts);
      const newDate: ExtractedDateRange = { id: uuid_v4(), start, end, selected: newImpacts.length > 0 };
      setLocalDates((prev) => prev.map((room, ri) => (ri === roomIndex ? [...room, newDate] : room)));
    },
    [catalogRooms, extraction.rooms, roomTypeOverrides],
  );

  const handleAddManualRoom = useCallback((catalogRoomId: string, catalogRoomName: string) => {
    setManualRooms((prev) => [...prev, { catalogRoomId, catalogRoomName, dates: [] }]);
  }, []);

  const handleRemoveManualRoom = useCallback((roomIndex: number) => {
    setManualRooms((prev) => prev.filter((_, i) => i !== roomIndex));
  }, []);

  const handleAddManualDate = useCallback(
    (roomIndex: number, start: string, end: string) => {
      setManualRooms((prev) => {
        const room = prev[roomIndex];
        const blackouts = catalogRooms.find((c) => c.id === room.catalogRoomId)?.blackoutDates ?? [];
        const dateImpacts = calculateAllReopenImpacts({ start, end }, blackouts);
        return prev.map((r, i) =>
          i === roomIndex
            ? {
                ...r,
                dates: [
                  ...r.dates,
                  { id: uuid_v4(), start, end, selected: dateImpacts.length > 0, impacts: dateImpacts },
                ],
              }
            : r,
        );
      });
    },
    [catalogRooms],
  );

  const handleRemoveManualDate = useCallback((roomIndex: number, dateId: string) => {
    setManualRooms((prev) =>
      prev.map((room, i) => (i === roomIndex ? { ...room, dates: room.dates.filter((d) => d.id !== dateId) } : room)),
    );
  }, []);

  const handleToggleManualDate = useCallback((roomIndex: number, dateId: string, selected: boolean) => {
    setManualRooms((prev) =>
      prev.map((room, i) =>
        i === roomIndex ? { ...room, dates: room.dates.map((d) => (d.id === dateId ? { ...d, selected } : d)) } : room,
      ),
    );
  }, []);

  const handleBlockToggle = useCallback((blockId: string, selected: boolean) => {
    setDeselectedBlockIds((prev) => {
      const next = new Set(prev);
      if (selected) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  }, []);

  const handleEditManualDate = useCallback(
    (roomIndex: number, dateId: string, newRange: { start: string; end: string }) => {
      setManualRooms((prev) =>
        prev.map((room, i) => {
          if (i !== roomIndex) return room;
          const blackouts = catalogRooms.find((c) => c.id === room.catalogRoomId)?.blackoutDates ?? [];
          const newImpacts = calculateAllReopenImpacts(newRange, blackouts);
          return {
            ...room,
            dates: room.dates.map((d) =>
              d.id === dateId
                ? { ...d, ...newRange, impacts: newImpacts, selected: newImpacts.length > 0 ? d.selected : false }
                : d,
            ),
          };
        }),
      );
    },
    [catalogRooms],
  );

  const handleApply = useCallback(() => {
    const rangesByRoomType = new Map<string, Array<{ start: string; end: string }>>();

    localDates.forEach((roomDates, roomIndex) => {
      const roomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
      if (!roomTypeId) return;
      const selected = roomDates
        .filter((date, di) => date.selected && (impacts[roomIndex]?.[di]?.length ?? 0) > 0)
        .map((d) => ({ start: d.start, end: d.end }));
      if (selected.length > 0) {
        const existing = rangesByRoomType.get(roomTypeId) ?? [];
        rangesByRoomType.set(roomTypeId, [...existing, ...selected]);
      }
    });

    manualRooms.forEach((room) => {
      const selected = room.dates
        .filter((d) => d.selected && d.impacts.length > 0)
        .map((d) => ({ start: d.start, end: d.end }));
      if (selected.length > 0) {
        const existing = rangesByRoomType.get(room.catalogRoomId) ?? [];
        rangesByRoomType.set(room.catalogRoomId, [...existing, ...selected]);
      }
    });

    const rooms: StopsellApplyPayload['rooms'] = [];
    rangesByRoomType.forEach((selectedRanges, roomTypeId) => {
      const blackouts = catalogRooms.find((c) => c.id === roomTypeId)?.blackoutDates ?? [];
      const merged = mergeDateRanges(selectedRanges);
      const filteredBlackouts = blackouts
        .map((bd) => ({ ...bd, blocks: bd.blocks.filter((b) => !deselectedBlockIds.has(b.id)) }))
        .filter((bd) => bd.blocks.length > 0);
      const { blocksToDelete, rangesToCreate } = calculateReopenPayloadParts(merged, filteredBlackouts);
      if (blocksToDelete.length > 0 || rangesToCreate.length > 0) {
        rooms.push({ roomTypeId, operations: [{ operation: 'reopen', blocksToDelete, rangesToCreate }] });
      }
    });

    onApply({ rooms });
  }, [
    localDates,
    manualRooms,
    catalogRooms,
    roomTypeOverrides,
    extraction.rooms,
    impacts,
    deselectedBlockIds,
    onApply,
  ]);

  const renderManualCard = useCallback(
    (room: ReopenManualRoom, roomIndex: number) => {
      const catalogRoom = catalogRoomById.get(room.catalogRoomId);
      return (
        <ReopenManualRoomCard
          key={room.catalogRoomId}
          room={room}
          roomIndex={roomIndex}
          blackoutDates={catalogRoom?.blackoutDates ?? []}
          catalogRoomRates={catalogRoom?.roomRates ?? []}
          deselectedBlockIds={deselectedBlockIds}
          onAddDate={handleAddManualDate}
          onEditDate={handleEditManualDate}
          onRemoveRoom={handleRemoveManualRoom}
          onRemoveDate={handleRemoveManualDate}
          onToggleDate={handleToggleManualDate}
          onBlockToggle={handleBlockToggle}
        />
      );
    },
    [
      catalogRoomById,
      deselectedBlockIds,
      handleAddManualDate,
      handleEditManualDate,
      handleRemoveManualRoom,
      handleRemoveManualDate,
      handleToggleManualDate,
      handleBlockToggle,
    ],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <LockOpenOutlinedIcon color="success" />
          <Typography variant="h6">Re-open Dates</Typography>
        </Stack>
        <Chip icon={<DescriptionOutlinedIcon />} label={fileName} size="small" sx={{ mt: 1 }} />
      </DialogTitle>

      <DialogContent dividers>
        {isBlackoutError ? (
          <Alert severity="error">Could not load existing blackout dates. Please close and try again.</Alert>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                mb: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectAllIndeterminate}
                    onChange={handleSelectAllChange}
                  />
                }
                label={<Typography fontWeight={500}>Select all rooms</Typography>}
              />
            </Box>

            <Stack gap={1.5}>
              {extraction.rooms.map((room, roomIndex) => (
                <ReopenRoomGroupRow
                  key={`${room.name}-${roomIndex}`}
                  roomIndex={roomIndex}
                  room={room}
                  localDates={localDates[roomIndex] ?? []}
                  impacts={impacts[roomIndex] ?? []}
                  catalogRooms={catalogRooms}
                  selectedRoomTypeId={roomTypeOverrides[roomIndex] ?? room.roomTypeId ?? ''}
                  deselectedBlockIds={deselectedBlockIds}
                  onDateChange={handleDateChange}
                  onToggleAll={handleRoomToggleAll}
                  onRoomTypeChange={handleRoomTypeOverride}
                  onEditDate={handleEditDate}
                  onDeleteDate={handleDeleteDate}
                  onAddExtractedDate={handleAddExtractedDate}
                  onBlockToggle={handleBlockToggle}
                />
              ))}
            </Stack>

            <AvailabilityManualRoomsSection
              catalogRooms={catalogRooms}
              extractedRoomTypeIds={extractedRoomTypeIds}
              manualRooms={manualRooms}
              onAddRoom={handleAddManualRoom}
              renderCard={renderManualCard}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{pluralizeToString('room', selectedRoomCount)}</strong>,{' '}
          <strong>{pluralizeToString('date range', selectedDateCount)}</strong> selected
        </Typography>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={isApplying || isBlackoutError || !anySelected || !allSelectedRoomsMapped}
          >
            {isApplying ? 'Applying...' : 'Apply Re-open'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
